import React, { useEffect, useMemo, useState } from "react";
import { Button, Spin, message } from "antd";
import { useTranslation } from "react-i18next";
import { useIsEnterpriseCustomer } from "util/platformPricing";
import { SONIC_PROSPECT_PRICING, formatProspectUsdTr } from "util/prospectBilling";
import {
  fetchProspectAccountBilling,
  openProspectCardPortal,
} from "util/prospectStripe";
import prospectStyles from "pages/SonicProspect/style.module.scss";
import styles from "./style.module.scss";

const SALES_CONTACT_MAIL =
  "mailto:sales@pivony.com?subject=Sonic%20Prospect%20Enterprise";

/**
 * Sonic Prospect card on /settings/plan. One plan: every feature for every
 * account, only Qualified Prospects are paid. Card and invoices live in the
 * Stripe portal of the usage subscription.
 */
const PlanSonicProspectBilling = ({ canManagePurchases, onRequestProduct }) => {
  const { t } = useTranslation();
  const isEnterprise = useIsEnterpriseCustomer();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [plansExpanded, setPlansExpanded] = useState(false);

  const {
    freeLifetimeQp: freeQp,
    tier1Limit: launchThreshold,
    tier1Price: launchRate,
    tier2Price: standardRate,
  } = SONIC_PROSPECT_PRICING;

  useEffect(() => {
    let stop = false;
    fetchProspectAccountBilling()
      .then((status) => {
        if (!stop) setAccount(status);
      })
      .finally(() => {
        if (!stop) setLoading(false);
      });
    return () => {
      stop = true;
    };
  }, []);

  const handleContactSales = () => {
    if (onRequestProduct) {
      onRequestProduct({
        productKey: "sonic_prospect_enterprise",
        productName: t("settings.plan_subscription_prospect_name"),
      });
      return;
    }
    window.location.assign(SALES_CONTACT_MAIL);
  };

  const handlePortal = async () => {
    setBusy(true);
    try {
      const url = await openProspectCardPortal({ returnPath: "/settings/plan" });
      window.location.assign(url);
    } catch (_) {
      message.error(t("sonic_prospect.plans_portal_error"));
      setBusy(false);
    }
  };

  const outcomeBlocks = useMemo(
    () => [
      {
        id: "free",
        title: t("sonic_prospect.outcome_start_free_title"),
        price: t("sonic_prospect.outcome_free_price"),
        note: t("sonic_prospect.outcome_start_free_note", { count: freeQp }),
        body: t("sonic_prospect.outcome_start_free_body", { count: freeQp }),
        badge: null,
      },
      {
        id: "launch",
        title: t("sonic_prospect.outcome_launch_title"),
        price: formatProspectUsdTr(launchRate),
        note: t("sonic_prospect.outcome_per_qp"),
        body: t("sonic_prospect.outcome_launch_body", {
          count: launchThreshold,
        }),
        badge: t("sonic_prospect.outcome_launch_badge"),
      },
      {
        id: "standard",
        title: t("sonic_prospect.outcome_standard_title"),
        price: formatProspectUsdTr(standardRate),
        note: t("sonic_prospect.outcome_per_qp"),
        body: t("sonic_prospect.outcome_standard_body", {
          count: launchThreshold,
        }),
        badge: null,
      },
    ],
    [t, launchRate, standardRate, launchThreshold, freeQp],
  );

  const canOpenPortal =
    canManagePurchases && !isEnterprise && Boolean(account?.card_saved);

  const plansPanel = (
    <>
      <p className={prospectStyles.plansCardBody} style={{ marginBottom: 16 }}>
        <strong>{t("sonic_prospect.outcome_headline")}</strong>
      </p>
      <p className={prospectStyles.plansCardBody} style={{ marginBottom: 20 }}>
        {t("sonic_prospect.outcome_support", {
          launchRate: formatProspectUsdTr(launchRate),
          standardRate: formatProspectUsdTr(standardRate),
          count: launchThreshold,
          free: freeQp,
        })}
      </p>

      <div className={prospectStyles.plansGrid}>
        {outcomeBlocks.map((block) => (
          <article
            key={block.id}
            className={`${prospectStyles.plansCard} ${
              block.id === "launch" ? prospectStyles.plansCardCurrent : ""
            }`}
          >
            {block.badge ? (
              <p className={prospectStyles.plansCardBadge}>{block.badge}</p>
            ) : null}
            <h3>{block.title}</h3>
            <p className={prospectStyles.plansPrice}>
              <span>{block.price}</span>
              <span>{block.note}</span>
            </p>
            <p className={prospectStyles.plansCardBody}>{block.body}</p>
          </article>
        ))}
      </div>

      <p className={prospectStyles.plansCardBody}>
        {t("sonic_prospect.outcome_enterprise_line")}{" "}
        {canManagePurchases ? (
          <Button type="link" size="small" onClick={handleContactSales}>
            {t("sonic_prospect.plans_contact_sales")}
          </Button>
        ) : null}
      </p>
    </>
  );

  return (
    <article className={styles.subscriptionCard}>
      {loading ? (
        <div className={prospectStyles.centered}>
          <Spin />
        </div>
      ) : (
        <>
          <div className={styles.subscriptionCardMain}>
            <div className={styles.subscriptionCardInfo}>
              <div className={styles.subscriptionCardHeader}>
                <h4 className={styles.subscriptionCardTitle}>
                  {t("settings.plan_subscription_prospect_name")}
                </h4>
              </div>
              <p className={styles.subscriptionCardMeta}>
                {isEnterprise
                  ? t("sonic_prospect.plans_enterprise_body")
                  : t("sonic_prospect.plans_one_plan_line")}
              </p>
              {!isEnterprise ? (
                <p className={styles.subscriptionCardMeta}>
                  {t("sonic_prospect.plans_conversations_line", {
                    answers: SONIC_PROSPECT_PRICING.fairUseAnswers,
                  })}
                </p>
              ) : null}
              {!isEnterprise && account ? (
                <p className={styles.subscriptionCardMeta}>
                  {account.card_last4
                    ? t("sonic_prospect.plans_card_line", {
                        brand: account.card_brand || t("sonic_prospect.plans_card_fallback"),
                        last4: account.card_last4,
                      })
                    : t("sonic_prospect.plans_no_card")}
                </p>
              ) : null}
            </div>
            <div className={styles.subscriptionCardActions}>
              {canManagePurchases && isEnterprise ? (
                <Button type="primary" onClick={handleContactSales}>
                  {t("sonic_prospect.plans_contact_sales")}
                </Button>
              ) : null}
              {canManagePurchases && !isEnterprise ? (
                <Button
                  type={plansExpanded ? "default" : "primary"}
                  onClick={() => setPlansExpanded((v) => !v)}
                  aria-expanded={plansExpanded}
                >
                  {plansExpanded
                    ? t("settings.plan_subscription_hide_plans")
                    : t("sonic_prospect.outcome_view_pricing")}
                </Button>
              ) : null}
              {canOpenPortal ? (
                <Button onClick={handlePortal} disabled={busy} loading={busy}>
                  {t("sonic_prospect.plans_manage_billing")}
                </Button>
              ) : null}
            </div>
          </div>
          {plansExpanded ? (
            <div className={styles.subscriptionUpgradePanel}>{plansPanel}</div>
          ) : null}
        </>
      )}
    </article>
  );
};

export default PlanSonicProspectBilling;
